import { useEffect, useState } from 'react';
import { isNil } from 'lodash/fp';
import styled from 'styled-components';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { ModalBodyWrapper, ModalWrapper } from '@mews-commander/ui';
import { Divider, getToken } from '@optimus-web/core';
import { useAppClockService } from '@mews/framework';
import { getCurrentEnterpriseUserPreferredServiceId } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import DashboardBox, { BorderedDashboardBox, DashboardPadding } from '../../components/DashboardBox';
import DashboardTitle from '../../components/DashboardTitle';
import EnterpriseLogo from '../EnterpriseLogo';
import { CommonWidgetsContainer } from '../commonWidgetsContainer';
import TaskStatisticsChart from '../TaskStatisticsChart';
import ServiceStatistics from '../ServiceStatistics';
import FinanceAndReports from '../../components/FinanceAndReports/FinanceAndReports';
import OrderStatistics from '../OrderStatistics';
import ResourceStatusStatistics from '../ResourceStatusStatistics';
import { CustomerStatistics } from '../CustomerStatistics';
import { OccupancyReportWrapper } from '../../components/OccupancyReport';
import { ChatTriggerButtonWrapper } from '../../components/ChatTriggerButtonComponent';
import { preselectDefaultServiceId } from '../../actions/serviceSelect';

const DashboardContainer = styled.div`
    display: flex;
    flex-flow: row wrap;
    background: ${getToken('backgroundStaticFlat')};
`;

const EnterpriseComponents = () => (
    <>
        <DashboardBox>
            <ServiceStatistics />
        </DashboardBox>

        <DashboardBox>
            <OrderStatistics />
            <ResourceStatusStatistics />
        </DashboardBox>

        <DashboardBox>
            <CustomerStatistics />
        </DashboardBox>

        <DashboardBox wide>
            <OccupancyReportWrapper />
        </DashboardBox>

        <DashboardBox>
            <FinanceAndReports />
        </DashboardBox>

        <DashboardBox>
            <CommonWidgetsContainer />
        </DashboardBox>
    </>
);

const NotEnterpriseComponents = () => (
    <>
        <DashboardBox>
            <FinanceAndReports />
        </DashboardBox>
    </>
);

const ApplicationProviderComponents = () => null;

const PortfolioComponents = () => null;

export interface DashboardProps {
    currentEnterpriseId: string;
    isDemo: boolean;
    isSalesForceChatInitialized: boolean;
    isApplicationProvider: boolean;
    isPortfolio: boolean;
    isServiceProvider: boolean;
}

export const Dashboard: React.FC<DashboardProps> = ({
    currentEnterpriseId,
    isDemo,
    isSalesForceChatInitialized,
    isServiceProvider,
    isApplicationProvider,
    isPortfolio,
}) => {
    const appClockService = useAppClockService();
    const dispatch = useCommanderThunkDispatch();
    const [isInitialized, setIsInitialized] = useState(false);
    const currentEnterpriseUserPreferredServiceId = useCommanderSelector(getCurrentEnterpriseUserPreferredServiceId);

    useEffect(() => {
        // This condition is to make sure the action preselectDefaultServiceId won't be called
        // before the services are fetched and stored in Redux
        if (currentEnterpriseUserPreferredServiceId) {
            dispatch(preselectDefaultServiceId());
            setIsInitialized(true);
        }
    }, [dispatch, currentEnterpriseUserPreferredServiceId]);

    if (!isInitialized) {
        return null;
    }

    if (isNil(currentEnterpriseId)) {
        return null;
    }

    let dashboardComponents = <NotEnterpriseComponents />;

    if (isServiceProvider) {
        dashboardComponents = <EnterpriseComponents />;
    } else if (isApplicationProvider) {
        dashboardComponents = <ApplicationProviderComponents />;
    } else if (isPortfolio) {
        dashboardComponents = <PortfolioComponents />;
    }

    return (
        <ModalWrapper>
            <ModalBodyWrapper>
                <DashboardContainer>
                    <BorderedDashboardBox>
                        <DashboardPadding>
                            <EnterpriseLogo />
                        </DashboardPadding>
                        {isServiceProvider ? (
                            <>
                                <Divider />
                                <DashboardPadding>
                                    <DashboardTitle textKey={TextKey.TaskPlural} />
                                    <TaskStatisticsChart
                                        dataTestId={DataTestId.TaskStatisticsChart}
                                        appClockService={appClockService}
                                    />
                                </DashboardPadding>
                            </>
                        ) : null}
                    </BorderedDashboardBox>
                    {dashboardComponents}
                    {isDemo && isSalesForceChatInitialized ? <ChatTriggerButtonWrapper /> : null}
                </DashboardContainer>
            </ModalBodyWrapper>
        </ModalWrapper>
    );
};
